import { useEffect, useState } from 'react'
import { FlowMeterSettings } from './charts/FlowMeterChart'
import { SensorProduct, UnitCategoryId } from '../common/enums'
import { determinePrecision } from '../common/utils'
import { api } from '../services'
import { UNIT_CONVERSIONS } from '../common/unitConversions'

const DEFAULT_FIELD_SIZE = 100

interface Props {
  sigfoxId: string
  sensorPort: string
  sensorProduct: SensorProduct
  sensorMetadata: any
  currentRate: number
  lastReading: number
  totalVolume: number
  totalVolumeInAcreFoot: number
  settings: FlowMeterSettings
}

export function BadgerSDIChartSubHeader({
  sigfoxId,
  sensorPort,
  sensorProduct,
  sensorMetadata,
  totalVolume,
  totalVolumeInAcreFoot,
  currentRate,
  lastReading,
  settings,
}: Props) {
  const [fieldSize, setFieldSize] = useState(DEFAULT_FIELD_SIZE)
  const [conversionScale, setConversionScale] = useState()
  const [conversionOffset, setConversionOffset] = useState()

  useEffect(() => {
    try {
      if (lastReading) {
        api.getSensorConfig(sigfoxId, lastReading).then((result) => {
          if (result && result.data && result.data[sensorPort]) {
            const { conversionScale, conversionOffset } = result.data[sensorPort]
            setConversionScale(conversionScale)
            setConversionOffset(conversionOffset)
          }
        })
      }
    } catch (error) {
      console.error({ error })
    }
  }, [sigfoxId, sensorPort, lastReading]) // eslint-disable-line react-hooks/exhaustive-deps

  const volumeConversions =
    sensorMetadata.length > 0
      ? sensorMetadata.find((metadata: any) => metadata.units.category.longName === 'Volume').units.conversions
      : null
  const volumeRateConversions = {
    ...UNIT_CONVERSIONS[UnitCategoryId.VolumeRate],
    current: {
      65: 'cubic meters / second',
      formula: '@',
    },
  }

  const ricSensorInfo = `${sensorPort}: ${sensorProduct}`

  function calculateWaterDepth() {
    if (fieldSize === 0 || isNaN(fieldSize)) {
      return '-'
    }

    const waterDepthInInches = (totalVolumeInAcreFoot / fieldSize) * 12
    let precision = determinePrecision(waterDepthInInches, 3)
    precision = precision < settings.volume.precision ? settings.volume.precision : precision
    return waterDepthInInches.toFixed(precision) + ' in'
  }

  return (
    <div className={tailwind.chartSubHeader}>
      <table className={tailwind.table}>
        <tbody>
          <tr className={tailwind.topTableRow}>
            <td>{ricSensorInfo}</td>
            <td>{`${lastReading}`}</td>
            <td>{`Rate: ${currentRate.toFixed(settings.volumeRate.precision)} ${
              volumeRateConversions === null
                ? 'acre-ft'
                : volumeRateConversions.abbreviations[settings.volumeRate.unitId]
            }`}</td>
            <td>
              <div className="flex flex-row">
                {`Totals: ${totalVolume.toFixed(settings.volume.precision)} ${
                  volumeConversions === null ? 'gal/min' : volumeConversions.abbreviations[settings.volume.unitId]
                }`}
              </div>
            </td>
          </tr>
          <tr className="text-sm">
            <td>{`K-Value: ${conversionScale ?? ''}`}</td>
            <td>{`Diameter: ${conversionOffset ?? ''}`}</td>
            <td className="pr-10">
              <div className="flex flex-row">
                <input
                  className={tailwind.inputText}
                  type="number"
                  id="field-size"
                  placeholder={'field size'}
                  value={fieldSize}
                  onChange={(e) => {
                    let value = parseInt(e.target.value)
                    if (value < 0) {
                      value = 0
                    }
                    setFieldSize(value)
                  }}
                />
                <div className="pt-0.5 pl-3">acres</div>
              </div>
            </td>
            <td>
              <div className="flex flex-row">{`Depth: ${calculateWaterDepth()}`}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const tailwind = {
  chartSubHeader: 'text-black text-l font-regular bg-[#DDD] pl-4 pr-4 w-full',

  table: 'table-fixed w-full',

  topTableRow: 'h-8 text-sm',

  inputText:
    'appearance-none border border-red-500 rounded w-full pt-0.5 px-1 mb-1 text-sm text-gray-700 leading-tight focus:outline-none cursor-pointer w-20',

  inputDropdown:
    'appearance-none border border-red-500 rounded w-full my-2 mr-3 py-2 px-3 text-sm text-gray-700 leading-tight focus:outline-none cursor-pointer',

  lockIcon: 'w-10 pt-2 pl-3 cursor-pointer',
}
